import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useThemeStore, THEMES, type ThemeName, type ColorMode } from '../stores/themeStore'
import { api, apiPut, apiPost, apiDelete } from '../lib/api'
import { useTabParam } from '../hooks/useTabParam'
import { ConfirmDialog } from '../components/ConfirmDialog'
import '../styles/profile.css'
import '../styles/ui.css'

const THEME_SWATCHES: Record<ThemeName, { dark: string[]; light: string[] }> = {
    professional: {
        dark: ['#4A2C82', '#6B44B8', '#D6134D', '#0B0B10', '#1A1A24'],
        light: ['#4A2C82', '#5B38A2', '#C4113F', '#FFFFFF', '#FFFFFF'],
    },
    bold: {
        dark: ['#8B5CF6', '#A78BFA', '#EC4899', '#030712', '#1E293B'],
        light: ['#7C3AED', '#8B5CF6', '#DB2777', '#FFFFFF', '#F1F5F9'],
    },
    playful: {
        dark: ['#E040A0', '#F472B6', '#FB923C', '#0E0A14', '#1E1830'],
        light: ['#DB2777', '#EC4899', '#EA580C', '#FFF7F9', '#FFFFFF'],
    },
    muted: {
        dark: ['#888888', '#AAAAAA', '#A1A1AA', '#121212', '#27272A'],
        light: ['#52525B', '#71717A', '#A1A1AA', '#FAFAFA', '#FFFFFF'],
    },
}

// ── Types ────────────────────────────────────────────────────────────
interface ConnectionProfileData {
    id: string
    name: string
    isActive: boolean
    legacyServer: string | null
    legacyPort: number
    legacyDatabase: string | null
    legacyUserId: string | null
    appHost: string | null
    appPort: number
    appDatabase: string | null
    appUsername: string | null
}

interface ProfileFormState {
    name: string
    legacyServer: string; legacyPort: string; legacyDatabase: string; legacyUserId: string; legacyPassword: string
    appHost: string; appPort: string; appDatabase: string; appUsername: string; appPassword: string
}

const emptyForm = (): ProfileFormState => ({
    name: '', legacyServer: '', legacyPort: '1433', legacyDatabase: '', legacyUserId: '', legacyPassword: '',
    appHost: '', appPort: '5432', appDatabase: '', appUsername: '', appPassword: ''
})

const profileToForm = (p: ConnectionProfileData): ProfileFormState => ({
    name: p.name,
    legacyServer: p.legacyServer ?? '', legacyPort: String(p.legacyPort),
    legacyDatabase: p.legacyDatabase ?? '', legacyUserId: p.legacyUserId ?? '', legacyPassword: '',
    appHost: p.appHost ?? '', appPort: String(p.appPort),
    appDatabase: p.appDatabase ?? '', appUsername: p.appUsername ?? '', appPassword: '',
})

// ── Component ────────────────────────────────────────────────────────
export default function AdminSettingsPage() {
    const token = useAuthStore(s => s.accessToken)
    const setSiteDefaults = useThemeStore(s => s.setSiteDefaults)

    const [siteTheme, setSiteTheme] = useState<ThemeName>('professional')
    const [siteColorMode, setSiteColorMode] = useState<ColorMode>('dark')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)
    const [activeTab, setActiveTab] = useTabParam(
        ['site', 'connections', 'backup', 'whitelist'] as const, 'site')
    const [statusMsg, setStatusMsg] = useState('')

    // Load current site defaults
    useEffect(() => {
        if (!token) return
        api('/api/admin/settings/theme', token)
            .then(data => {
                if (data?.theme) setSiteTheme(data.theme)
                if (data?.colorMode) setSiteColorMode(data.colorMode)
            })
            .catch(() => { /* defaults already set */ })
            .finally(() => setLoading(false))
    }, [token])

    const handleSave = async () => {
        if (!token) return
        setSaving(true)
        try {
            await apiPut('/api/admin/settings/theme', token, {
                theme: siteTheme,
                colorMode: siteColorMode,
            })
            // Update the global store so all users see this default
            setSiteDefaults(siteTheme, siteColorMode)
            setSaved(true)
            setTimeout(() => setSaved(false), 2000)
        } catch {
            setStatusMsg('❌ Failed to save site theme setting.')
            setTimeout(() => setStatusMsg(''), 3000)
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <div className="page-header">
                <h1>Site Settings</h1>
                <p>Configure site-wide defaults</p>
            </div>

            {/* Tabs */}
            <div className="tabs-container" style={{ display: 'flex', gap: 'var(--space-2)', borderBottom: '1px solid var(--color-border)', marginBottom: 'var(--space-6)' }}>
                <button
                    className={`tab-btn ${activeTab === 'site' ? 'active' : ''}`}
                    onClick={() => setActiveTab('site')}
                    style={{ padding: 'var(--space-2) var(--space-4)', background: 'none', border: 'none', borderBottom: activeTab === 'site' ? '2px solid var(--color-primary)' : '2px solid transparent', color: activeTab === 'site' ? 'var(--color-primary)' : 'var(--color-text-secondary)', fontWeight: activeTab === 'site' ? 600 : 400, cursor: 'pointer', fontSize: 'var(--text-md)' }}
                >
                    Site settings
                </button>
                <button
                    className={`tab-btn ${activeTab === 'connections' ? 'active' : ''}`}
                    onClick={() => setActiveTab('connections')}
                    style={{ padding: 'var(--space-2) var(--space-4)', background: 'none', border: 'none', borderBottom: activeTab === 'connections' ? '2px solid var(--color-primary)' : '2px solid transparent', color: activeTab === 'connections' ? 'var(--color-primary)' : 'var(--color-text-secondary)', fontWeight: activeTab === 'connections' ? 600 : 400, cursor: 'pointer', fontSize: 'var(--text-md)' }}
                >
                    Connections/API
                </button>
                <button
                    className={`tab-btn ${activeTab === 'backup' ? 'active' : ''}`}
                    onClick={() => setActiveTab('backup')}
                    style={{ padding: 'var(--space-2) var(--space-4)', background: 'none', border: 'none', borderBottom: activeTab === 'backup' ? '2px solid var(--color-primary)' : '2px solid transparent', color: activeTab === 'backup' ? 'var(--color-primary)' : 'var(--color-text-secondary)', fontWeight: activeTab === 'backup' ? 600 : 400, cursor: 'pointer', fontSize: 'var(--text-md)' }}
                >
                    Backup/Restore
                </button>
                <button
                    className={`tab-btn ${activeTab === 'whitelist' ? 'active' : ''}`}
                    onClick={() => setActiveTab('whitelist')}
                    style={{ padding: 'var(--space-2) var(--space-4)', background: 'none', border: 'none', borderBottom: activeTab === 'whitelist' ? '2px solid var(--color-primary)' : '2px solid transparent', color: activeTab === 'whitelist' ? 'var(--color-primary)' : 'var(--color-text-secondary)', fontWeight: activeTab === 'whitelist' ? 600 : 400, cursor: 'pointer', fontSize: 'var(--text-md)' }}
                >
                    Submission Whitelist
                </button>
            </div>

            <div className="settings-page">
                {activeTab === 'site' && (
                <div className="profile-section">
                    <div className="profile-section-title">
                        Site Default Theme
                        {saved && <span className="settings-save-status"> ✓ Saved</span>}
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 'var(--space-4)', color: 'var(--color-text-tertiary)' }}>
                            Loading…
                        </div>
                    ) : (
                        <>
                            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                                Choose the default theme and color mode for all users. Users can override this on their profile page.
                            </p>

                            <div className="theme-picker-grid">
                                {THEMES.map(t => (
                                    <div
                                        key={t.name}
                                        className={`theme-card${siteTheme === t.name ? ' selected' : ''}`}
                                        onClick={() => setSiteTheme(t.name)}
                                    >
                                        <div className="theme-card-preview">
                                            {THEME_SWATCHES[t.name][siteColorMode].map((color, i) => (
                                                <div key={i} className="swatch" style={{ background: color }} />
                                            ))}
                                        </div>
                                        <div className="theme-card-name">{t.label}</div>
                                        <div className="theme-card-desc">{t.description}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="color-mode-row">
                                <div className="color-mode-labels">
                                    <span className={`color-mode-label${siteColorMode === 'light' ? ' active' : ''}`}>☀️ Light</span>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={siteColorMode === 'dark'}
                                            onChange={() => setSiteColorMode(m => m === 'dark' ? 'light' : 'dark')}
                                        />
                                        <span className="toggle-slider" />
                                    </label>
                                    <span className={`color-mode-label${siteColorMode === 'dark' ? ' active' : ''}`}>🌙 Dark</span>
                                </div>
                            </div>

                            <div style={{ marginTop: 'var(--space-5)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                {statusMsg && <span style={{ color: 'var(--color-danger)', marginRight: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>{statusMsg}</span>}
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving…' : 'Save Site Default'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
                )}

                {/* Database Connection / Email Profiles */}
                {activeTab === 'connections' && (
                    <>
                        <DatabaseConnectionsSection token={token} />
                        <EmailConfigurationSection token={token} />
                        <AutomatedNotificationsSection token={token} />
                        <ReportDefaultsSection token={token} />
                    </>
                )}

                {/* Backup & Restore */}
                {activeTab === 'backup' && (
                    <BackupRestoreSection token={token} />
                )}

                {/* Submission whitelist */}
                {activeTab === 'whitelist' && (
                    <SubmissionWhitelistSection token={token} />
                )}
            </div>
        </>
    )
}

// ── Database Connections Section ──────────────────────────────────────
function DatabaseConnectionsSection({ token }: { token: string | null }) {
    const [profiles, setProfiles] = useState<ConnectionProfileData[]>([])
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [form, setForm] = useState<ProfileFormState>(emptyForm())
    const [isNew, setIsNew] = useState(false)
    const [loadingProfiles, setLoadingProfiles] = useState(true)
    const [savingProfile, setSavingProfile] = useState(false)
    const [statusMsg, setStatusMsg] = useState('')
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [legacyTestResult, setLegacyTestResult] = useState<{ success: boolean; message: string } | null>(null)
    const [appTestResult, setAppTestResult] = useState<{ success: boolean; message: string } | null>(null)
    const [testing, setTesting] = useState<'legacy' | 'app' | null>(null)

    const showStatus = (msg: string) => {
        setStatusMsg(msg)
        setTimeout(() => setStatusMsg(''), 3000)
    }

    const fetchProfiles = useCallback(async () => {
        if (!token) return
        try {
            const data = await api('/api/admin/settings/database', token)
            setProfiles(data.profiles ?? [])
            // Select the active profile, or the first one
            const active = (data.profiles ?? []).find((p: ConnectionProfileData) => p.isActive)
            if (active) {
                setSelectedId(active.id)
                setForm(profileToForm(active))
                setIsNew(false)
            } else if (data.profiles?.length > 0) {
                setSelectedId(data.profiles[0].id)
                setForm(profileToForm(data.profiles[0]))
                setIsNew(false)
            } else {
                setSelectedId(null)
                setForm(emptyForm())
                setIsNew(true)
            }
        } catch {
            /* empty */
        } finally {
            setLoadingProfiles(false)
        }
    }, [token])

    useEffect(() => { fetchProfiles() }, [fetchProfiles])

    const handleProfileChange = (id: string) => {
        if (id === '__new__') {
            setSelectedId(null)
            setForm(emptyForm())
            setIsNew(true)
            setLegacyTestResult(null)
            setAppTestResult(null)
            return
        }
        const p = profiles.find(x => x.id === id)
        if (p) {
            setSelectedId(p.id)
            setForm(profileToForm(p))
            setIsNew(false)
            setLegacyTestResult(null)
            setAppTestResult(null)
        }
    }

    const updateField = (field: keyof ProfileFormState, value: string) => {
        setForm(f => ({ ...f, [field]: value }))
    }

    const handleSaveProfile = async () => {
        if (!token) return
        setSavingProfile(true)
        try {
            if (isNew) {
                const body = {
                    name: form.name,
                    legacyServer: form.legacyServer || null,
                    legacyPort: parseInt(form.legacyPort) || 1433,
                    legacyDatabase: form.legacyDatabase || null,
                    legacyUserId: form.legacyUserId || null,
                    legacyPassword: form.legacyPassword || null,
                    appHost: form.appHost || null,
                    appPort: parseInt(form.appPort) || 5432,
                    appDatabase: form.appDatabase || null,
                    appUsername: form.appUsername || null,
                    appPassword: form.appPassword || null,
                }
                await apiPost('/api/admin/settings/database/profiles', token, body)
                showStatus('✓ Profile created')
            } else if (selectedId) {
                const body = {
                    name: form.name || null,
                    legacyServer: form.legacyServer || null,
                    legacyPort: parseInt(form.legacyPort) || null,
                    legacyDatabase: form.legacyDatabase || null,
                    legacyUserId: form.legacyUserId || null,
                    legacyPassword: form.legacyPassword || null,
                    appHost: form.appHost || null,
                    appPort: parseInt(form.appPort) || null,
                    appDatabase: form.appDatabase || null,
                    appUsername: form.appUsername || null,
                    appPassword: form.appPassword || null,
                }
                await apiPut(`/api/admin/settings/database/profiles/${selectedId}`, token, body)
                showStatus('✓ Profile updated')
            }
            await fetchProfiles()
            setIsNew(false)
        } catch (e: any) {
            showStatus('❌ ' + (e?.message || 'Failed to save profile.'))
        } finally {
            setSavingProfile(false)
        }
    }

    const executeDelete = async () => {
        if (!token || !selectedId) return
        setShowDeleteConfirm(false)
        try {
            await apiDelete(`/api/admin/settings/database/profiles/${selectedId}`, token)
            showStatus('✓ Profile deleted')
            await fetchProfiles()
        } catch (e: any) {
            showStatus('❌ ' + (e?.message || 'Failed to delete profile.'))
        }
    }

    const handleSetActive = async () => {
        if (!token || !selectedId) return
        try {
            await apiPut(`/api/admin/settings/database/active/${selectedId}`, token, {})
            showStatus('✓ Active profile switched — connections updated')
            await fetchProfiles()
        } catch (e: any) {
            showStatus('❌ ' + (e?.message || 'Failed to switch active profile.'))
        }
    }

    const handleTest = async (type: 'legacy' | 'app') => {
        if (!token) return
        setTesting(type)
        if (type === 'legacy') setLegacyTestResult(null)
        else setAppTestResult(null)

        try {
            // For a saved profile, send its id so the server can fall back to the stored
            // password when the (masked) password field is left blank.
            const profileId = isNew ? undefined : (selectedId ?? undefined)
            const body = type === 'legacy'
                ? { type: 'legacy', server: form.legacyServer, port: parseInt(form.legacyPort) || 1433, database: form.legacyDatabase, userId: form.legacyUserId, password: form.legacyPassword, profileId }
                : { type: 'app', server: form.appHost, port: parseInt(form.appPort) || 5432, database: form.appDatabase, userId: form.appUsername, password: form.appPassword, profileId }

            const data = await apiPost('/api/admin/settings/database/test', token, body)
            const result = { success: data.success, message: data.message || (data.success ? 'Connected.' : 'Connection failed — no details available.') }
            if (type === 'legacy') setLegacyTestResult(result)
            else setAppTestResult(result)
        } catch (e: any) {
            // apiPost throws new Error(responseText) — try to parse JSON from it
            let msg = 'Test request failed.'
            try {
                const parsed = JSON.parse(e?.message || '{}')
                msg = parsed?.error || parsed?.message || parsed?.detail || e?.message || msg
            } catch {
                msg = e?.message || msg
            }
            const result = { success: false, message: msg }
            if (type === 'legacy') setLegacyTestResult(result)
            else setAppTestResult(result)
        } finally {
            setTesting(null)
        }
    }

    const selected = profiles.find(p => p.id === selectedId)
    const isActive = selected?.isActive ?? false

    const handleUseCurrent = async () => {
        if (!token) return
        try {
            const data = await api('/api/admin/settings/database/current', token)
            setForm(f => ({
                ...f,
                name: f.name || 'New Profile',
                legacyServer: data.legacy?.server ?? '',
                legacyPort: String(data.legacy?.port ?? 1433),
                legacyDatabase: data.legacy?.database ?? '',
                legacyUserId: data.legacy?.userId ?? '',
                legacyPassword: '',
                appHost: data.app?.host ?? '',
                appPort: String(data.app?.port ?? 5432),
                appDatabase: data.app?.database ?? '',
                appUsername: data.app?.username ?? '',
                appPassword: '',
            }))
            showStatus('✓ Filled from current connections (passwords must be re-entered)')
        } catch {
            showStatus('⚠ Could not fetch current connections')
        }
    }

    return (
        <div className="profile-section db-connections-section">
            <div className="profile-section-title">Database Connections</div>

            {loadingProfiles ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-4)', color: 'var(--color-text-tertiary)' }}>
                    Loading profiles…
                </div>
            ) : (
                <>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                        Manage named connection profiles. Switch between Dev, Staging, and Production databases without restarting.
                    </p>

                    {/* Profile Selector */}
                    <div className="profile-selector-row">
                        <select
                            value={isNew ? '__new__' : (selectedId ?? '')}
                            onChange={e => handleProfileChange(e.target.value)}
                        >
                            {profiles.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.name}{p.isActive ? ' ★' : ''}
                                </option>
                            ))}
                            <option value="__new__">＋ New Profile</option>
                        </select>
                        {isActive && <span className="active-badge">Active</span>}
                        <button className="btn btn-secondary" style={{ fontSize: 'var(--text-xs)' }} onClick={handleUseCurrent}>
                            Use Current
                        </button>
                    </div>

                    {/* Profile Name */}
                    <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-1)', display: 'block' }}>
                            Profile Name
                        </label>
                        <input
                            value={form.name}
                            onChange={e => updateField('name', e.target.value)}
                            placeholder="e.g. Dev Local, Production"
                            style={{
                                width: '100%', maxWidth: 300, padding: 'var(--space-2) var(--space-3)',
                                background: 'var(--color-surface-card)', border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)',
                                fontSize: 'var(--text-sm)', boxSizing: 'border-box' as const
                            }}
                        />
                    </div>

                    {/* Connection Cards */}
                    <div className="connection-cards">
                        {/* MSSQL Card */}
                        <div className="connection-card">
                            <div className="connection-card-header">
                                <div className="connection-card-title">
                                    🗄️ Legacy Database (MSSQL)
                                </div>
                                <span className="readonly-badge">Read-Only</span>
                            </div>

                            <div className="connection-card-row">
                                <div className="form-group">
                                    <label>Server Name</label>
                                    <input value={form.legacyServer} onChange={e => updateField('legacyServer', e.target.value)} placeholder="sql01.corp.local" />
                                </div>
                                <div className="form-group">
                                    <label>Port</label>
                                    <input value={form.legacyPort} onChange={e => updateField('legacyPort', e.target.value)} placeholder="1433" />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Database</label>
                                <input value={form.legacyDatabase} onChange={e => updateField('legacyDatabase', e.target.value)} placeholder="RetailDB" />
                            </div>
                            <div className="connection-card-row">
                                <div className="form-group">
                                    <label>User ID</label>
                                    <input value={form.legacyUserId} onChange={e => updateField('legacyUserId', e.target.value)} placeholder="readonly" />
                                </div>
                                <div className="form-group">
                                    <label>Password</label>
                                    <input type="password" value={form.legacyPassword} onChange={e => updateField('legacyPassword', e.target.value)} placeholder={selectedId ? '••••••••' : ''} />
                                </div>
                            </div>

                            <div className="connection-card-actions">
                                <button className="btn btn-secondary" style={{ fontSize: 'var(--text-xs)' }}
                                    onClick={() => handleTest('legacy')}
                                    disabled={!form.legacyServer || (!form.legacyPassword && !selectedId) || testing === 'legacy'}
                                >
                                    {testing === 'legacy' ? 'Testing…' : 'Test Connection'}
                                </button>
                            </div>
                            {legacyTestResult && (
                                <div className={`db-test-result ${legacyTestResult.success ? 'success' : 'error'}`}>
                                    <span>{legacyTestResult.success ? '✓' : '✗'}</span>
                                    <span>{legacyTestResult.message}</span>
                                </div>
                            )}
                        </div>

                        {/* PostgreSQL Card */}
                        <div className="connection-card">
                            <div className="connection-card-header">
                                <div className="connection-card-title">
                                    🐘 App Database (PostgreSQL)
                                </div>
                            </div>

                            <div className="connection-card-row">
                                <div className="form-group">
                                    <label>Host</label>
                                    <input value={form.appHost} onChange={e => updateField('appHost', e.target.value)} placeholder="localhost" />
                                </div>
                                <div className="form-group">
                                    <label>Port</label>
                                    <input value={form.appPort} onChange={e => updateField('appPort', e.target.value)} placeholder="5432" />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Database</label>
                                <input value={form.appDatabase} onChange={e => updateField('appDatabase', e.target.value)} placeholder="puresoft_modern" />
                            </div>
                            <div className="connection-card-row">
                                <div className="form-group">
                                    <label>Username</label>
                                    <input value={form.appUsername} onChange={e => updateField('appUsername', e.target.value)} placeholder="postgres" />
                                </div>
                                <div className="form-group">
                                    <label>Password</label>
                                    <input type="password" value={form.appPassword} onChange={e => updateField('appPassword', e.target.value)} placeholder={selectedId ? '••••••••' : ''} />
                                </div>
                            </div>

                            <div className="connection-card-actions">
                                <button className="btn btn-secondary" style={{ fontSize: 'var(--text-xs)' }}
                                    onClick={() => handleTest('app')}
                                    disabled={!form.appHost || (!form.appPassword && !selectedId) || testing === 'app'}
                                >
                                    {testing === 'app' ? 'Testing…' : 'Test Connection'}
                                </button>
                            </div>
                            {appTestResult && (
                                <div className={`db-test-result ${appTestResult.success ? 'success' : 'error'}`}>
                                    <span>{appTestResult.success ? '✓' : '✗'}</span>
                                    <span>{appTestResult.message}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="profile-actions-bar">
                        {statusMsg && <span className="status-msg">{statusMsg}</span>}
                        
                        <div style={{ flexGrow: 1 }} />

                        {showDeleteConfirm ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', background: 'rgba(239, 68, 68, 0.1)', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-md)' }}>
                                <span style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-danger)' }}>Delete profile?</span>
                                <button className="btn btn-secondary" style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-3)' }} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                                <button className="btn btn-primary" style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-3)', background: 'var(--color-danger)', borderColor: 'var(--color-danger)' }} onClick={executeDelete}>Yes, Delete</button>
                            </div>
                        ) : (
                            selectedId && !isActive && (
                                <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)} style={{ fontSize: 'var(--text-xs)' }}>
                                    Delete Profile
                                </button>
                            )
                        )}
                        {selectedId && !isActive && (
                            <button className="btn btn-secondary" onClick={handleSetActive} style={{ fontSize: 'var(--text-xs)' }}>
                                Set as Active
                            </button>
                        )}
                        <button
                            className="btn btn-primary"
                            onClick={handleSaveProfile}
                            disabled={!form.name.trim() || savingProfile}
                        >
                            {savingProfile ? 'Saving…' : isNew ? 'Create Profile' : 'Save Profile'}
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}

// ── Backup & Restore Section ─────────────────────────────────────────

const SECTION_LABELS: Record<string, string> = {
    appSettings: 'App Settings',
    users: 'Users',
    stores: 'Stores',
    storeAssignments: 'Store Assignments',
    accessRequests: 'Access Requests',
    staffClassifications: 'Staff Classifications',
    dashboardWidgets: 'Dashboard Widgets',
    dashboardLayouts: 'Dashboard Layouts',
    savedReports: 'Saved Reports',
    auditLog: 'Audit Log',
}

interface SectionSummary {
    inserted: number
    updated: number
    deleted: number
    skipped: number
}

interface ImportResult {
    preview: boolean
    mode: string
    message: string
    summary: Record<string, SectionSummary>
}

function BackupRestoreSection({ token }: { token: string | null }) {
    const [tab, setTab] = useState<'export' | 'import'>('export')
    const [exportJson, setExportJson] = useState('')
    const [exporting, setExporting] = useState(false)
    const [exported, setExported] = useState(false)
    const [importJson, setImportJson] = useState('')
    const [importMode, setImportMode] = useState<'update' | 'replace'>('update')
    const [importResult, setImportResult] = useState<ImportResult | null>(null)
    const [importing, setImporting] = useState(false)
    const [previewing, setPreviewing] = useState(false)
    const [copied, setCopied] = useState(false)
    const [statusMsg, setStatusMsg] = useState('')
    const [showImportConfirm, setShowImportConfirm] = useState(false)

    const showStatus = (msg: string) => {
        setStatusMsg(msg)
        setTimeout(() => setStatusMsg(''), 5000)
    }

    const handleExport = async () => {
        if (!token) return
        setExporting(true)
        setExported(false)
        try {
            const data = await api('/api/admin/settings/export', token)
            const json = JSON.stringify(data, null, 2)
            setExportJson(json)
            setExported(true)
        } catch (e: any) {
            showStatus('❌ ' + (e?.message || 'Export failed.'))
        } finally {
            setExporting(false)
        }
    }

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(exportJson)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // Fallback
            const ta = document.createElement('textarea')
            ta.value = exportJson
            document.body.appendChild(ta)
            ta.select()
            document.execCommand('copy')
            document.body.removeChild(ta)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    const handlePreview = async () => {
        if (!token || !importJson.trim()) return
        setPreviewing(true)
        setImportResult(null)
        try {
            const res = await fetch(
                `${(import.meta as any).env?.VITE_API_BASE_URL ?? ''}/api/admin/settings/import?mode=${importMode}&preview=true`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: importJson,
                }
            )
            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: res.statusText }))
                throw new Error(err.error || err.detail || 'Preview failed.')
            }
            const data: ImportResult = await res.json()
            setImportResult(data)
        } catch (e: any) {
            showStatus('❌ ' + (e?.message || 'Preview failed.'))
        } finally {
            setPreviewing(false)
        }
    }

    const executeImport = async () => {
        if (!token || !importJson.trim()) return
        setShowImportConfirm(false)
        setImporting(true)
        setImportResult(null)
        try {
            const res = await fetch(
                `${(import.meta as any).env?.VITE_API_BASE_URL ?? ''}/api/admin/settings/import?mode=${importMode}&preview=false`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: importJson,
                }
            )
            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: res.statusText }))
                throw new Error(err.error || err.detail || 'Import failed.')
            }
            const data: ImportResult = await res.json()
            setImportResult(data)
            showStatus('✅ Import successful.')
        } catch (e: any) {
            showStatus('❌ ' + (e?.message || 'Import failed. Changes were rolled back.'))
        } finally {
            setImporting(false)
        }
    }

    // Parse the import JSON to get section counts for the preview
    const parsedSections = (() => {
        try {
            const obj = JSON.parse(importJson)
            return Object.entries(SECTION_LABELS)
                .map(([key, label]) => ({
                    key,
                    label,
                    count: Array.isArray(obj[key]) ? obj[key].length : 0,
                }))
                .filter(s => s.count > 0)
        } catch {
            return []
        }
    })()

    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: 'var(--space-2) var(--space-3)',
        background: 'var(--color-surface-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--color-text-primary)',
        fontSize: 'var(--text-sm)',
        boxSizing: 'border-box',
    }

    return (
        <div className="profile-section">
            <div className="profile-section-title">Backup & Restore</div>

            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                Export application settings as JSON for backup or migration between environments. Import settings to restore or clone an environment.
            </p>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 'var(--space-4)' }}>
                <button
                    className={`btn ${tab === 'export' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setTab('export')}
                    style={{ fontSize: 'var(--text-sm)', borderRadius: 'var(--radius-md) 0 0 var(--radius-md)' }}
                >
                    Export
                </button>
                <button
                    className={`btn ${tab === 'import' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setTab('import')}
                    style={{ fontSize: 'var(--text-sm)', borderRadius: '0 var(--radius-md) var(--radius-md) 0' }}
                >
                    Import
                </button>
            </div>

            {/* Export Tab */}
            {tab === 'export' && (
                <div>
                    <textarea
                        readOnly
                        value={exportJson}
                        placeholder={'Click "Generate Export" to create a snapshot of all application settings\u2026'}
                        style={{
                            ...inputStyle,
                            height: 300,
                            fontFamily: 'monospace',
                            fontSize: 'var(--text-xs)',
                            resize: 'vertical',
                            marginBottom: 'var(--space-3)',
                        }}
                    />
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button className="btn btn-primary" onClick={handleExport} disabled={exporting}>
                            {exporting ? 'Exporting…' : '📤 Generate Export'}
                        </button>
                        {exported && (
                            <button className="btn btn-secondary" onClick={handleCopy}>
                                {copied ? '✓ Copied!' : '📋 Copy to Clipboard'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Import Tab */}
            {tab === 'import' && (
                <div>
                    {/* Mode selector */}
                    <div style={{ marginBottom: 'var(--space-3)' }}>
                        <label style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 'var(--space-1)' }}>
                            Import Mode
                        </label>
                        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
                                <input type="radio" name="importMode" checked={importMode === 'update'} onChange={() => setImportMode('update')} />
                                <strong>Update</strong> — only upsert records from the file
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
                                <input type="radio" name="importMode" checked={importMode === 'replace'} onChange={() => setImportMode('replace')} />
                                <strong>Replace</strong> — purge section, then insert
                            </label>
                        </div>
                        {importMode === 'replace' && (
                            <div style={{ marginTop: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                                ⚠️ Replace mode will <strong>delete all existing records</strong> in each section before importing. Use Preview first to verify.
                            </div>
                        )}
                    </div>

                    {/* Paste area */}
                    <textarea
                        placeholder="Paste exported JSON here…"
                        value={importJson}
                        onChange={e => setImportJson(e.target.value)}
                        style={{
                            ...inputStyle,
                            height: 200,
                            fontFamily: 'monospace',
                            fontSize: 'var(--text-xs)',
                            resize: 'vertical',
                            marginBottom: 'var(--space-3)',
                        }}
                    />

                    {/* Section summary from parsed JSON */}
                    {parsedSections.length > 0 && (
                        <div style={{ marginBottom: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                            <strong>Detected sections:</strong>{' '}
                            {parsedSections.map(s => `${s.label} (${s.count})`).join(', ')}
                        </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', alignItems: 'center' }}>
                        <button
                            className="btn btn-secondary"
                            onClick={handlePreview}
                            disabled={!importJson.trim() || previewing}
                        >
                            {previewing ? 'Previewing…' : '🔍 Preview (Dry Run)'}
                        </button>
                        
                        {showImportConfirm ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', background: 'rgba(239, 68, 68, 0.1)', padding: 'var(--space-1) var(--space-4)', borderRadius: 'var(--radius-md)' }}>
                                <span style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-danger)' }}>Apply {importMode} import?</span>
                                <button className="btn btn-secondary" style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-3)' }} onClick={() => setShowImportConfirm(false)}>Cancel</button>
                                <button className="btn btn-primary" style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-3)', background: 'var(--color-danger)', borderColor: 'var(--color-danger)' }} onClick={executeImport}>Yes, Import</button>
                            </div>
                        ) : (
                            <button
                                className="btn btn-primary"
                                onClick={() => setShowImportConfirm(true)}
                                disabled={!importJson.trim() || importing}
                            >
                                {importing ? 'Importing…' : '✅ Apply Import'}
                            </button>
                        )}
                        
                        {statusMsg && <div style={{ fontSize: 'var(--text-sm)', color: statusMsg.startsWith('❌') ? 'var(--color-danger)' : 'var(--color-success)', marginLeft: 'var(--space-3)' }}>{statusMsg}</div>}
                    </div>

                    {/* Result summary */}
                    {importResult && (
                        <div style={{
                            padding: 'var(--space-3)',
                            background: importResult.preview ? 'var(--color-surface-card)' : 'rgba(34, 197, 94, 0.1)',
                            border: `1px solid ${importResult.preview ? 'var(--color-border)' : 'rgba(34, 197, 94, 0.3)'}`,
                            borderRadius: 'var(--radius-md)',
                        }}>
                            <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>
                                {importResult.preview ? '🔍 Preview Results' : '✅ Import Complete'} — {importResult.mode} mode
                            </div>
                            <table style={{ width: '100%', fontSize: 'var(--text-xs)', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                        <th style={{ textAlign: 'left', padding: 'var(--space-1) var(--space-2)' }}>Section</th>
                                        <th style={{ textAlign: 'center', padding: 'var(--space-1) var(--space-2)' }}>Inserted</th>
                                        <th style={{ textAlign: 'center', padding: 'var(--space-1) var(--space-2)' }}>Updated</th>
                                        <th style={{ textAlign: 'center', padding: 'var(--space-1) var(--space-2)' }}>Deleted</th>
                                        <th style={{ textAlign: 'center', padding: 'var(--space-1) var(--space-2)' }}>Skipped</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(importResult.summary)
                                        .filter(([, v]) => v.inserted || v.updated || v.deleted || v.skipped)
                                        .map(([key, v]) => (
                                            <tr key={key} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                                <td style={{ padding: 'var(--space-1) var(--space-2)' }}>{SECTION_LABELS[key] ?? key}</td>
                                                <td style={{ textAlign: 'center', padding: 'var(--space-1) var(--space-2)', color: v.inserted ? '#22c55e' : 'inherit' }}>{v.inserted || '—'}</td>
                                                <td style={{ textAlign: 'center', padding: 'var(--space-1) var(--space-2)', color: v.updated ? '#f59e0b' : 'inherit' }}>{v.updated || '—'}</td>
                                                <td style={{ textAlign: 'center', padding: 'var(--space-1) var(--space-2)', color: v.deleted ? '#ef4444' : 'inherit' }}>{v.deleted || '—'}</td>
                                                <td style={{ textAlign: 'center', padding: 'var(--space-1) var(--space-2)', color: v.skipped ? '#6b7280' : 'inherit' }}>{v.skipped || '—'}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// ── Email Configuration Section (AWS SES) ────────────────────────────

function EmailConfigurationSection({ token }: { token: string | null }) {
    const [region, setRegion] = useState('')
    const [accessKeyId, setAccessKeyId] = useState('')
    const [secretAccessKey, setSecretAccessKey] = useState('')
    const [verifiedFromEmail, setVerifiedFromEmail] = useState('')
    const [fromName, setFromName] = useState('')
    const [bccEmail, setBccEmail] = useState('')
    const [hasSecret, setHasSecret] = useState(false)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [statusMsg, setStatusMsg] = useState('')
    const [testEmail, setTestEmail] = useState('')
    const [testing, setTesting] = useState(false)
    const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false)

    const showStatus = (msg: string) => {
        setStatusMsg(msg)
        setTimeout(() => setStatusMsg(''), 4000)
    }

    const fetchEmailSettings = useCallback(async () => {
        if (!token) return
        try {
            const data = await api('/api/admin/settings/email', token)
            setRegion(data.region ?? '')
            setAccessKeyId(data.accessKeyId ?? '')
            setHasSecret(data.hasSecret ?? false)
            setVerifiedFromEmail(data.verifiedFromEmail ?? '')
            setFromName(data.fromName ?? '')
            setBccEmail(data.bccEmail ?? '')
        } catch {
            showStatus('⚠ Could not fetch email settings')
        } finally {
            setLoading(false)
        }
    }, [token])

    useEffect(() => { fetchEmailSettings() }, [fetchEmailSettings])

    const handleSave = async (confirmed: boolean = false) => {
        if (!token) return
        
        if (hasSecret && secretAccessKey && !confirmed) {
            setShowOverwriteConfirm(true)
            return
        }
        
        setShowOverwriteConfirm(false)
        setSaving(true)
        try {
            await apiPut('/api/admin/settings/email', token, {
                region,
                accessKeyId,
                secretAccessKey: secretAccessKey || undefined, // only send if non-empty
                verifiedFromEmail,
                fromName,
                bccEmail,
            })
            showStatus('✓ Email settings saved')
            setSecretAccessKey('') // clear it since it's saved now
            await fetchEmailSettings()
        } catch (e: any) {
            showStatus('❌ ' + (e?.message || 'Failed to save email settings.'))
        } finally {
            setSaving(false)
        }
    }

    const handleTestEmail = async () => {
        if (!token || !testEmail) return
        setTesting(true)
        try {
            const result = await apiPost('/api/admin/settings/email/test', token, {
                toEmail: testEmail
            })
            showStatus('✓ ' + result.message)
        } catch (e: any) {
            // Check if there is an inner .error string
            let msg = 'Test email failed.'
            try {
                const parsed = JSON.parse(e?.message || '{}')
                msg = parsed?.error || parsed?.message || parsed?.detail || e?.message || msg
            } catch {
                msg = e?.message || msg
            }
            showStatus('❌ ' + msg)
        } finally {
            setTesting(false)
            setTestEmail('')
        }
    }

    const inputStyle: React.CSSProperties = {
        width: '100%', maxWidth: 400, padding: 'var(--space-2) var(--space-3)',
        background: 'var(--color-surface-card)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)',
        fontSize: 'var(--text-sm)', boxSizing: 'border-box'
    }

    return (
        <div className="profile-section db-connections-section" style={{ marginTop: 'var(--space-6)' }}>
            <div className="profile-section-title">Email Configuration (Amazon SES)</div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-4)', color: 'var(--color-text-tertiary)' }}>
                    Loading email settings…
                </div>
            ) : (
                <>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                        Configure Amazon Simple Email Service (SES) to enable email sending capabilities such as notifications and access requests.
                    </p>

                    <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
                        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 'var(--space-1)' }}>AWS Region</label>
                        <input value={region} onChange={e => setRegion(e.target.value)} placeholder="us-east-1" style={inputStyle} />
                    </div>

                    <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
                        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 'var(--space-1)' }}>Access Key ID</label>
                        <input value={accessKeyId} onChange={e => setAccessKeyId(e.target.value)} placeholder="AKIAIOSFODNN7EXAMPLE" style={inputStyle} />
                    </div>

                    <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
                        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 'var(--space-1)' }}>
                            Secret Access Key {hasSecret && <span style={{ color: 'var(--color-success)', fontWeight: 400 }}> (✓ Encrypted and Saved)</span>}
                        </label>
                        <input
                            type="password"
                            value={secretAccessKey}
                            onChange={e => setSecretAccessKey(e.target.value)}
                            placeholder={hasSecret ? '••••••••••••••••••••••••••••••••••••••••' : 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY'}
                            style={inputStyle}
                        />
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>Leave blank to keep existing encrypted secret.</div>
                    </div>

                    <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
                        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 'var(--space-1)' }}>Verified From Email</label>
                        <input value={verifiedFromEmail} onChange={e => setVerifiedFromEmail(e.target.value)} placeholder="noreply@yourdomain.com" style={inputStyle} />
                    </div>

                    <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
                        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 'var(--space-1)' }}>Sender Name</label>
                        <input value={fromName} onChange={e => setFromName(e.target.value)} placeholder="Puresoft Dashboard" style={inputStyle} />
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>The display name recipients see instead of the raw address (e.g. "Puresoft Dashboard" rather than "noreply"). Defaults to "Puresoft Dashboard" if left blank.</div>
                    </div>

                    <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
                        <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 'var(--space-1)' }}>Default BCC Email (Optional)</label>
                        <input value={bccEmail} onChange={e => setBccEmail(e.target.value)} placeholder="audit@yourdomain.com" style={inputStyle} />
                    </div>

                    <div className="profile-actions-bar" style={{ marginTop: 'var(--space-4)' }}>
                        {statusMsg && <span className="status-msg" style={{ maxWidth: '300px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{statusMsg}</span>}
                        
                        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                            <input
                                value={testEmail}
                                onChange={e => setTestEmail(e.target.value)}
                                placeholder="Test destination email"
                                style={{
                                    ...inputStyle,
                                    width: 200,
                                    padding: 'var(--space-2)',
                                    marginBottom: 0
                                }}
                            />
                            <button
                                className="btn btn-secondary"
                                onClick={handleTestEmail}
                                disabled={testing || !testEmail}
                                style={{ fontSize: 'var(--text-xs)' }}
                            >
                                {testing ? 'Sending…' : 'Send Test Email'}
                            </button>
                        </div>
                        
                        <div style={{ flexGrow: 1 }} />

                        {showOverwriteConfirm ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', background: 'rgba(239, 68, 68, 0.1)', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-md)' }}>
                                <span style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-danger)' }}>Overwrite encrypted secret?</span>
                                <button className="btn btn-secondary" style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-3)' }} onClick={() => setShowOverwriteConfirm(false)}>Cancel</button>
                                <button className="btn btn-primary" style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-3)', background: 'var(--color-danger)', borderColor: 'var(--color-danger)' }} onClick={() => handleSave(true)}>Yes, Overwrite</button>
                            </div>
                        ) : (
                            <button
                                className="btn btn-primary"
                                onClick={() => handleSave(false)}
                                disabled={saving}
                            >
                                {saving ? 'Saving…' : 'Save Email Settings'}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

// ── Automated Notifications (kill switches) ───────────────────────────

function AutomatedNotificationsSection({ token }: { token: string | null }) {
    const [neverOut, setNeverOut] = useState(true)
    const [roadmap, setRoadmap] = useState(true)
    const [reports, setReports] = useState(true)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [statusMsg, setStatusMsg] = useState('')

    const showStatus = (msg: string) => {
        setStatusMsg(msg)
        setTimeout(() => setStatusMsg(''), 4000)
    }

    const fetchSettings = useCallback(async () => {
        if (!token) return
        try {
            const data = await api('/api/admin/settings/notifications', token)
            setNeverOut(data.neverOutAutomatedEnabled ?? true)
            setRoadmap(data.roadmapAutomatedEnabled ?? true)
            setReports(data.reportsAutomatedEnabled ?? true)
        } catch {
            showStatus('⚠ Could not fetch notification settings')
        } finally {
            setLoading(false)
        }
    }, [token])

    useEffect(() => { fetchSettings() }, [fetchSettings])

    // Persist immediately on toggle; roll the switch back if the save fails.
    const save = async (patch: { neverOutAutomatedEnabled?: boolean; roadmapAutomatedEnabled?: boolean; reportsAutomatedEnabled?: boolean }) => {
        if (!token || saving) return
        const prevNeverOut = neverOut
        const prevRoadmap = roadmap
        const prevReports = reports
        if (patch.neverOutAutomatedEnabled !== undefined) setNeverOut(patch.neverOutAutomatedEnabled)
        if (patch.roadmapAutomatedEnabled !== undefined) setRoadmap(patch.roadmapAutomatedEnabled)
        if (patch.reportsAutomatedEnabled !== undefined) setReports(patch.reportsAutomatedEnabled)
        setSaving(true)
        try {
            await apiPut('/api/admin/settings/notifications', token, patch)
            showStatus('✓ Saved')
        } catch (e: any) {
            setNeverOut(prevNeverOut)
            setRoadmap(prevRoadmap)
            setReports(prevReports)
            showStatus('❌ ' + (e?.message || 'Failed to save.'))
        } finally {
            setSaving(false)
        }
    }

    const rows: { label: string; hint: string; on: boolean; toggle: () => void }[] = [
        {
            label: 'Never Out inventory alerts',
            hint: 'Scheduled low-stock and overstock digest emails. Turning this off pauses the automatic checks — the "Send test" and "Run now" buttons still send.',
            on: neverOut,
            toggle: () => save({ neverOutAutomatedEnabled: !neverOut }),
        },
        {
            label: 'Roadmap notifications',
            hint: 'Automatic roadmap emails (phase changes, digests). Queued updates flush once this is turned back on.',
            on: roadmap,
            toggle: () => save({ roadmapAutomatedEnabled: !roadmap }),
        },
        {
            label: 'Scheduled report emails',
            hint: 'Daily sales PDFs and PO lockdown reminders. Turning this off pauses the scheduled sends — the "Send test" and "Run now" buttons on the Scheduled Reports page still send.',
            on: reports,
            toggle: () => save({ reportsAutomatedEnabled: !reports }),
        },
    ]

    return (
        <div className="profile-section db-connections-section" style={{ marginTop: 'var(--space-6)' }}>
            <div className="profile-section-title">Automated Notifications</div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-4)', color: 'var(--color-text-tertiary)' }}>
                    Loading notification settings…
                </div>
            ) : (
                <>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                        Pause emails that the system sends on its own. Notifications a person triggers directly
                        (test sends, access requests, submission confirmations) are never affected.
                        {statusMsg && <span style={{ marginLeft: 'var(--space-3)', fontWeight: 500 }}>{statusMsg}</span>}
                    </p>

                    {rows.map(r => (
                        <div
                            key={r.label}
                            style={{
                                display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)',
                                padding: 'var(--space-3) 0', borderTop: '1px solid var(--color-border)',
                            }}
                        >
                            <label className="toggle-switch" style={{ marginTop: 2, flexShrink: 0 }}>
                                <input type="checkbox" checked={r.on} disabled={saving} onChange={r.toggle} />
                                <span className="toggle-slider" />
                            </label>
                            <div>
                                <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                    {r.label}
                                    <span style={{
                                        marginLeft: 'var(--space-2)', fontSize: 'var(--text-xs)', fontWeight: 500,
                                        color: r.on ? 'var(--color-success)' : 'var(--color-text-tertiary)',
                                    }}>
                                        {r.on ? 'On' : 'Paused'}
                                    </span>
                                </div>
                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>
                                    {r.hint}
                                </div>
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    )
}

// ── Scheduled report mailer defaults ──────────────────────────────────
// The values the legacy DailyReportMailer had baked into its source and XML config.
function ReportDefaultsSection({ token }: { token: string | null }) {
    const [lagDays, setLagDays] = useState(1)
    const [leadDays, setLeadDays] = useState(2)
    const [deepLinkBase, setDeepLinkBase] = useState('')
    const [warehouseGuids, setWarehouseGuids] = useState('')
    const [alertsEnabled, setAlertsEnabled] = useState(true)
    const [alertRecipients, setAlertRecipients] = useState('')
    const [alertThrottleHours, setAlertThrottleHours] = useState(6)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [statusMsg, setStatusMsg] = useState('')

    const showStatus = (msg: string) => {
        setStatusMsg(msg)
        setTimeout(() => setStatusMsg(''), 4000)
    }

    const fetchSettings = useCallback(async () => {
        if (!token) return
        try {
            const data = await api('/api/admin/settings/reports', token)
            setLagDays(data.dailySalesLagDays ?? 1)
            setLeadDays(data.poLockdownLeadDays ?? 2)
            setDeepLinkBase(data.poLockdownDeepLinkBase ?? '')
            setWarehouseGuids(data.warehouseStoreGuids ?? '')
            setAlertsEnabled(data.alertsEnabled ?? true)
            setAlertRecipients(data.alertRecipients ?? '')
            setAlertThrottleHours(data.alertThrottleHours ?? 6)
        } catch {
            showStatus('⚠ Could not fetch report settings')
        } finally {
            setLoading(false)
        }
    }, [token])

    useEffect(() => { fetchSettings() }, [fetchSettings])

    const save = async () => {
        if (!token || saving) return
        setSaving(true)
        try {
            await apiPut('/api/admin/settings/reports', token, {
                dailySalesLagDays: lagDays,
                poLockdownLeadDays: leadDays,
                poLockdownDeepLinkBase: deepLinkBase,
                warehouseStoreGuids: warehouseGuids,
                alertsEnabled,
                alertRecipients,
                alertThrottleHours,
            })
            showStatus('✓ Saved')
        } catch (e: any) {
            showStatus('❌ ' + (e?.message || 'Failed to save.'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="profile-section db-connections-section" style={{ marginTop: 'var(--space-6)' }}>
            <div className="profile-section-title">Scheduled Report Defaults</div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-4)', color: 'var(--color-text-tertiary)' }}>
                    Loading report settings…
                </div>
            ) : (
                <>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                        Applies to every store. Per-store send times and recipients live on the
                        Scheduled Reports page.
                        {statusMsg && <span style={{ marginLeft: 'var(--space-3)', fontWeight: 500 }}>{statusMsg}</span>}
                    </p>

                    <div className="form-field">
                        <label className="form-label">Daily sales report covers</label>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                            <input className="form-input" style={{ width: 90 }} type="number" min={0} max={30}
                                   value={lagDays}
                                   onChange={e => setLagDays(parseInt(e.target.value, 10) || 0)} />
                            <span style={{ fontSize: 'var(--text-sm)' }}>day(s) ago</span>
                        </div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>
                            1 means a 7am run reports on yesterday. Raise this if stores are slow to sync to Head Office.
                        </div>
                    </div>

                    <div className="form-field">
                        <label className="form-label">PO lockdown reminders sent</label>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                            <input className="form-input" style={{ width: 90 }} type="number" min={0} max={60}
                                   value={leadDays}
                                   onChange={e => setLeadDays(parseInt(e.target.value, 10) || 0)} />
                            <span style={{ fontSize: 'var(--text-sm)' }}>day(s) before a PO locks down</span>
                        </div>
                    </div>

                    <div className="form-field">
                        <label className="form-label">PO link base</label>
                        <input className="form-input" type="text" value={deepLinkBase}
                               onChange={e => setDeepLinkBase(e.target.value)} />
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>
                            The PO's id is appended to this to build the "open this order" link in reminder emails.
                        </div>
                    </div>

                    <div className="form-field">
                        <label className="form-label">Warehouse store ids</label>
                        <input className="form-input" type="text" value={warehouseGuids}
                               onChange={e => setWarehouseGuids(e.target.value)} />
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>
                            Comma-separated. Stock at these stores appears in the "Warehouse" column of the PO breakdown.
                        </div>
                    </div>

                    <div style={{
                        borderTop: '1px solid var(--color-border)',
                        margin: 'var(--space-5) 0 var(--space-4)',
                        paddingTop: 'var(--space-4)',
                    }}>
                        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                            Critical alerts
                        </div>
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 0, marginBottom: 'var(--space-4)' }}>
                            Emails an administrator when a report fails, or when the scheduler was
                            found to have stopped running. If email itself is the thing that's
                            broken, these can't get through — use the <strong>Problems</strong>
                            filter on the Scheduled Reports activity log instead.
                        </p>

                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                            <label className="toggle-switch" style={{ marginTop: 2, flexShrink: 0 }}>
                                <input type="checkbox" checked={alertsEnabled} disabled={saving}
                                       onChange={e => setAlertsEnabled(e.target.checked)} />
                                <span className="toggle-slider" />
                            </label>
                            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                Send critical alert emails
                                <span style={{
                                    marginLeft: 'var(--space-2)', fontSize: 'var(--text-xs)', fontWeight: 500,
                                    color: alertsEnabled ? 'var(--color-success)' : 'var(--color-text-tertiary)',
                                }}>
                                    {alertsEnabled ? 'On' : 'Off'}
                                </span>
                            </div>
                        </div>

                        <div className="form-field">
                            <label className="form-label">Alert recipients</label>
                            <input className="form-input" type="text" value={alertRecipients}
                                   placeholder="it@vpo.ca, someone@vpo.ca"
                                   onChange={e => setAlertRecipients(e.target.value)} />
                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>
                                Comma-separated. With none set, problems are still recorded in the activity log but nobody is emailed.
                            </div>
                        </div>

                        <div className="form-field">
                            <label className="form-label">Don't repeat the same alert for</label>
                            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                                <input className="form-input" style={{ width: 90 }} type="number" min={0} max={168}
                                       value={alertThrottleHours}
                                       onChange={e => setAlertThrottleHours(parseInt(e.target.value, 10) || 0)} />
                                <span style={{ fontSize: 'var(--text-sm)' }}>hour(s)</span>
                            </div>
                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>
                                A failing report retries every ten minutes; without this you'd get an email each time. Counted separately per store and report, so one noisy store won't hide another starting to fail.
                            </div>
                        </div>
                    </div>

                    <button className="btn btn-primary" onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save report settings'}
                    </button>
                </>
            )}
        </div>
    )
}

// ── Submission domain whitelist ───────────────────────────────────────
function SubmissionWhitelistSection({ token }: { token: string | null }) {
    const [domains, setDomains] = useState<string[]>([])
    const [loading, setLoading] = useState(true)
    const [newDomain, setNewDomain] = useState('')
    const [adding, setAdding] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [confirmRemove, setConfirmRemove] = useState<string | null>(null)

    const load = useCallback(() => {
        if (!token) return
        setLoading(true)
        api('/api/admin/roadmap/settings/allowed-domains', token)
            .then((data: any) => setDomains(data?.domains ?? []))
            .finally(() => setLoading(false))
    }, [token])

    useEffect(() => { load() }, [load])

    const handleAdd = async () => {
        if (!newDomain.trim() || !token) return
        setAdding(true); setError(null)
        try {
            await apiPost('/api/admin/roadmap/settings/allowed-domains', token, { domain: newDomain.trim() })
            setNewDomain('')
            load()
        } catch (e: any) {
            let msg = 'Failed to add domain.'
            try { msg = JSON.parse(e?.message)?.error ?? e?.message ?? msg } catch { msg = e?.message ?? msg }
            setError(msg)
        } finally { setAdding(false) }
    }

    const handleRemove = async (domain: string) => {
        if (!token) return
        try {
            await apiDelete(`/api/admin/roadmap/settings/allowed-domains/${encodeURIComponent(domain)}`, token)
            load()
        } catch { /* swallow */ }
    }

    return (
        <div className="profile-section">
            <div className="profile-section-title">Submission Domain Whitelist</div>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', lineHeight: 1.6 }}>
                Only email addresses from the listed domains can submit ideas anonymously (without signing in) on the
                public roadmap page. Authenticated users are always allowed to submit.
            </p>

            {loading ? (
                <div style={{ padding: 'var(--space-4)', color: 'var(--color-text-tertiary)' }}>Loading…</div>
            ) : (
                <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', maxWidth: 480 }}>
                        {domains.length === 0 && (
                            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>
                                No domains whitelisted — anonymous submission is disabled.
                            </div>
                        )}
                        {domains.map(d => (
                            <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                                <span style={{ fontSize: 18 }}>✉️</span>
                                <span style={{ flex: 1, fontFamily: 'monospace', fontSize: 'var(--text-sm)', fontWeight: 600 }}>@{d}</span>
                                <button className="btn btn-danger" style={{ padding: '2px 10px', fontSize: 'var(--text-xs)' }} onClick={() => setConfirmRemove(d)}>Remove</button>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', maxWidth: 400 }}>
                        <input
                            className="form-input"
                            placeholder="domain.com"
                            value={newDomain}
                            onChange={e => setNewDomain(e.target.value.replace('@', ''))}
                            onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        />
                        <button className="btn btn-primary" onClick={handleAdd} disabled={!newDomain.trim() || adding}>
                            {adding ? 'Adding…' : 'Add domain'}
                        </button>
                    </div>
                    {error && <div style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>{error}</div>}
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-2)' }}>
                        Enter just the domain portion, e.g. <code>vpo.ca</code> — not the full email address.
                    </div>
                </>
            )}

            {confirmRemove && (
                <ConfirmDialog
                    title="Remove domain?"
                    message={`Remove @${confirmRemove} from the whitelist? Existing pending submissions from this domain are unaffected.`}
                    confirmLabel="Remove"
                    danger
                    onCancel={() => setConfirmRemove(null)}
                    onConfirm={() => { const d = confirmRemove; setConfirmRemove(null); handleRemove(d) }}
                />
            )}
        </div>
    )
}
